/** @format */

import { CsvImport } from "../../components/CsvImport";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CsvValidation } from "../../components/CsvValidation";
import { PdfImport, type ImportPdfKeys } from "../../components/PdfImport";
import { PdfImportStart } from "../../components/PdfImportStart";
import { FLAG_IMPORT_CSV_TAB } from "../../featureFlags/constants";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";

interface TabButtonProps {
	activeTab: TabKey;
	onTabChange: (tab: TabKey) => void;
	tabKeys: readonly TabKey[];
	tabLabels: Record<TabKey, string>;
}
interface TabContentProps {
	activeTab: TabKey;
	onPdfUploaded: (keys: ImportPdfKeys | null, paperFile?: File | null) => void;
}

const getTabStyle = (isActive: boolean) =>
	isActive
		? "h-[36px] py-[6px] px-[16px] gap-1 text-[14px] whitespace-nowrap text-(--admin-color-primary-strong) cursor-pointer border-b-2 font-roboto font-semibold"
		: "h-[36px] py-[6px] px-4 gap-1 text-[14px] whitespace-nowrap cursor-pointer text-slate-500 font-semibold";

const TAB_KEYS = ["csvImport", "pdfImport"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TabButtons = ({ activeTab, onTabChange, tabKeys, tabLabels }: TabButtonProps) => {
	return (
		<div className="flex" role="tablist">
			{tabKeys.map((key) => (
				<button
					type="button"
					key={key}
					role="tab"
					aria-selected={activeTab === key}
					onClick={() => onTabChange(key)}
					className={getTabStyle(activeTab === key)}
				>
					{tabLabels[key]}
				</button>
			))}
		</div>
	);
};
const TabContent = ({ activeTab, onPdfUploaded }: TabContentProps) => {
	const [file, setFile] = useState<File | null>(null);

	if (activeTab === "csvImport") {
		return (
			<div className="mt-4 px-6">
				<CsvImport onFileReady={setFile} />
				{file && <CsvValidation csvFile={file} />}
			</div>
		);
	}
	if (activeTab === "pdfImport") {
		return (
			<div className="mt-4 px-6">
				<PdfImport onKeysChange={onPdfUploaded} />
			</div>
		);
	}
};

export const AdminImportQuestionPage = () => {
	const { t } = useTranslation();
	const canShowCsvImport = useFeatureFlag(FLAG_IMPORT_CSV_TAB);
	const [activeTab, setActiveTab] = useState<TabKey>("pdfImport");
	const [pdfKeys, setPdfKeys] = useState<ImportPdfKeys | null>(null);
	const [paperFile, setPaperFile] = useState<File | null>(null);
	const visibleTabs: readonly TabKey[] = canShowCsvImport ? TAB_KEYS : (["pdfImport"] as const);
	const selectedTab = visibleTabs.includes(activeTab) ? activeTab : "pdfImport";

	const tabLabels: Record<TabKey, string> = {
		csvImport: "Import CSV",
		pdfImport: "Import PDF",
	};

	return (
		<div>
			<div className="mb-4">
				<h2 className="text-[1.75rem] font-bold text-(--admin-color-primary)">Import Questions</h2>
			</div>

			{visibleTabs.length > 1 && (
				<div>
					<TabButtons
						activeTab={selectedTab}
						onTabChange={(tab) => {
							setActiveTab(tab);
							setPdfKeys(null);
							setPaperFile(null);
						}}
						tabKeys={visibleTabs}
						tabLabels={tabLabels}
					/>
				</div>
			)}
			<div>
				<TabContent
					activeTab={selectedTab}
					onPdfUploaded={(keys, file) => {
						setPdfKeys(keys);
						setPaperFile(file ?? null);
					}}
				/>
				{selectedTab === "pdfImport" && pdfKeys && (
					<div className="px-6">
						<p className="mt-4 text-sm text-green-700">{t("pdfImport.allUploaded")}</p>
						<PdfImportStart keys={pdfKeys} paperFile={paperFile} />
					</div>
				)}
			</div>
		</div>
	);
};
